import { useEffect, useState } from 'react'
import { MessageSquare, Send, X, CheckCircle2, AlertCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { ExerciseFeedback, SessionFeedback } from '@/types/ai-training'
import { submitCombinedFeedback, saveExerciseFeedback, saveSessionFeedback } from '@/services/feedbackService'
import { useLanguage } from '@/contexts/LanguageContext'
import { getApiBaseUrl } from '@/lib/apiBaseUrl'
import { supabase } from '@/lib/supabase'

interface TrainingFeedbackInputProps {
  sessionId: string
  userId: string
  exercises?: Array<{ id: string; name: string }>
  showTriggerButton?: boolean
  defaultOpen?: boolean
  inlineMode?: boolean
  titleOverride?: string
  subtitleOverride?: string
  submitLabelOverride?: string
  onOpenChange?: (isOpen: boolean) => void
  onFeedbackSubmitted?: () => void
}

export default function TrainingFeedbackInput({
  sessionId,
  userId,
  exercises = [],
  showTriggerButton = true,
  defaultOpen = false,
  inlineMode = false,
  titleOverride,
  subtitleOverride,
  submitLabelOverride,
  onOpenChange,
  onFeedbackSubmitted
}: TrainingFeedbackInputProps) {
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  // Session-level feedback
  const [overallRating, setOverallRating] = useState<number>(3)
  const [difficultyVsExpected, setDifficultyVsExpected] = useState<'too_easy' | 'optimal' | 'too_hard'>('optimal')
  const [energyAfter, setEnergyAfter] = useState<number>(5)
  const [motivationAfter, setMotivationAfter] = useState<number>(5)
  const [pacingFeedback, setPacingFeedback] = useState<'too_fast' | 'optimal' | 'too_slow'>('optimal')
  const [durationFeedback, setDurationFeedback] = useState<'too_short' | 'optimal' | 'too_long'>('optimal')
  const [varietyFeedback, setVarietyFeedback] = useState<'too_repetitive' | 'optimal' | 'too_scattered'>('optimal')
  const [feltProgress, setFeltProgress] = useState<boolean>(true)
  const [achievedGoals, setAchievedGoals] = useState<string[]>([])
  const [missingElements, setMissingElements] = useState<string[]>([])
  const [wantMoreOf, setWantMoreOf] = useState<string[]>([])
  const [wantLessOf, setWantLessOf] = useState<string[]>([])
  const [bestPart, setBestPart] = useState<string>('')
  const [mostChallenging, setMostChallenging] = useState<string>('')
  const [suggestions, setSuggestions] = useState<string>('')
  const [wouldRepeat, setWouldRepeat] = useState<boolean>(true)

  // Exercise-level feedback
  const [exerciseFeedbacks, setExerciseFeedbacks] = useState<Record<string, Partial<ExerciseFeedback>>>({})

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true)
      onOpenChange?.(true)
    }
  }, [defaultOpen, onOpenChange])

  const openForm = () => {
    setIsOpen(true)
    onOpenChange?.(true)
  }

  const closeForm = () => {
    setIsOpen(false)
    onOpenChange?.(false)
  }

  const handleExerciseFeedbackChange = (exerciseId: string, field: keyof ExerciseFeedback, value: any) => {
    setExerciseFeedbacks(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        exercise_id: exerciseId,
        session_id: sessionId,
        user_id: userId,
        timestamp: new Date().toISOString(),
        [field]: value
      }
    }))
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Create session feedback
      const sessionFeedback: SessionFeedback = {
        session_id: sessionId,
        user_id: userId,
        completed_at: new Date().toISOString(),
        overall_rating: overallRating,
        difficulty_vs_expected: difficultyVsExpected,
        energy_after_training: energyAfter,
        motivation_after_training: motivationAfter,
        pacing_feedback: pacingFeedback,
        duration_feedback: durationFeedback,
        variety_feedback: varietyFeedback,
        felt_progress: feltProgress,
        achieved_goals: achievedGoals,
        missing_elements: missingElements,
        want_more_of: wantMoreOf,
        want_less_of: wantLessOf,
        best_part: bestPart,
        most_challenging: mostChallenging,
        suggestions: suggestions,
        would_repeat: wouldRepeat
      }

      // Create exercise feedbacks
      const exerciseFeedbacksArray: ExerciseFeedback[] = exercises.map(ex => {
        const feedback = exerciseFeedbacks[ex.id] || {}
        return {
          exercise_id: ex.id,
          session_id: sessionId,
          user_id: userId,
          timestamp: new Date().toISOString(),
          completed: feedback.completed ?? true,
          completion_percentage: feedback.completion_percentage ?? 100,
          actual_duration: feedback.actual_duration,
          difficulty_rating: feedback.difficulty_rating ?? 5,
          technique_confidence: feedback.technique_confidence ?? 5,
          enjoyment_rating: feedback.enjoyment_rating ?? 3,
          target_body_areas: feedback.target_body_areas ?? [],
          perceived_effectiveness: feedback.perceived_effectiveness ?? 5,
          modifications_used: feedback.modifications_used,
          notes: feedback.notes
        }
      })

      // Save locally
      saveSessionFeedback(sessionFeedback)
      saveExerciseFeedback(sessionId, exerciseFeedbacksArray)
      submitCombinedFeedback(sessionFeedback, exerciseFeedbacksArray)

      // Submit to backend
      try {
        const response = await fetch(`${getApiBaseUrl()}/training/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: userId,
            session_feedback: sessionFeedback,
            exercise_feedback: exerciseFeedbacksArray
          })
        })

        if (!response.ok) {
          throw new Error('Failed to submit feedback to backend')
        }
      } catch (backendError) {
        console.warn('Backend submission failed, but feedback saved locally:', backendError)
        // Continue anyway - feedback is saved locally
      }

      setSubmitStatus('success')
      setTimeout(() => {
        closeForm()
        onFeedbackSubmitted?.()
        // Reset form
        setOverallRating(3)
        setDifficultyVsExpected('optimal')
        setEnergyAfter(5)
        setMotivationAfter(5)
        setPacingFeedback('optimal')
        setDurationFeedback('optimal')
        setVarietyFeedback('optimal')
        setFeltProgress(true)
        setAchievedGoals([])
        setMissingElements([])
        setWantMoreOf([])
        setWantLessOf([])
        setBestPart('')
        setMostChallenging('')
        setSuggestions('')
        setWouldRepeat(true)
        setExerciseFeedbacks({})
      }, 2000)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setSubmitStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) {
    if (inlineMode) {
      return null
    }

    if (!showTriggerButton) {
      return null
    }

    return (
      <Button
        onClick={openForm}
        className="fixed bottom-6 right-6 z-50 shadow-lg"
        variant="primary"
      >
        <MessageSquare className="w-5 h-5 mr-2" />
        {t('training.feedback.submit') || 'Feedback geben'}
      </Button>
    )
  }

  return (
    <div className={inlineMode ? '' : 'fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'}>
      <Card className={inlineMode ? 'w-full' : 'w-full max-w-4xl max-h-[90vh] overflow-y-auto'}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {titleOverride || t('training.feedback.title') || 'Training Feedback'}
            </h2>
            <Button
              variant="ghost"
              onClick={closeForm}
              className="p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {subtitleOverride && (
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-300">
              {subtitleOverride}
            </p>
          )}

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-green-800 dark:text-green-200">
                {t('training.feedback.success') || 'Feedback erfolgreich übermittelt!'}
              </span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">
                {t('training.feedback.error') || 'Fehler beim Übermitteln des Feedbacks'}
              </span>
            </div>
          )}

          {/* Session Feedback */}
          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">
                {t('training.feedback.session.title') || 'Session-Bewertung'}
              </h3>

              {/* Overall Rating */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                  {t('training.feedback.overallRating') || 'Gesamtbewertung'} (1-5)
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setOverallRating(rating)}
                      className={`w-12 h-12 rounded-lg border-2 transition-colors ${
                        overallRating >= rating
                          ? 'bg-yellow-400 border-yellow-500'
                          : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty vs Expected */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                  {t('training.feedback.difficulty') || 'Schwierigkeit'}
                </label>
                <div className="flex gap-2">
                  {(['too_easy', 'optimal', 'too_hard'] as const).map(option => (
                    <Button
                      key={option}
                      type="button"
                      variant={difficultyVsExpected === option ? 'primary' : 'ghost'}
                      onClick={() => setDifficultyVsExpected(option)}
                      className="flex-1"
                    >
                      {t(`training.feedback.difficulty.${option}`) || option}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Energy & Motivation */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.energyAfter') || 'Energie nach Training'} (1-10)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={energyAfter}
                    onChange={(e) => setEnergyAfter(parseInt(e.target.value) || 5)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.motivationAfter') || 'Motivation nach Training'} (1-10)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={motivationAfter}
                    onChange={(e) => setMotivationAfter(parseInt(e.target.value) || 5)}
                  />
                </div>
              </div>

              {/* Pacing, Duration, Variety */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.pacing') || 'Tempo'}
                  </label>
                  <select
                    value={pacingFeedback}
                    onChange={(e) => setPacingFeedback(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="too_fast">{t('training.feedback.pacing.too_fast') || 'Zu schnell'}</option>
                    <option value="optimal">{t('training.feedback.pacing.optimal') || 'Optimal'}</option>
                    <option value="too_slow">{t('training.feedback.pacing.too_slow') || 'Zu langsam'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.duration') || 'Dauer'}
                  </label>
                  <select
                    value={durationFeedback}
                    onChange={(e) => setDurationFeedback(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="too_short">{t('training.feedback.duration.too_short') || 'Zu kurz'}</option>
                    <option value="optimal">{t('training.feedback.duration.optimal') || 'Optimal'}</option>
                    <option value="too_long">{t('training.feedback.duration.too_long') || 'Zu lang'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.variety') || 'Vielfalt'}
                  </label>
                  <select
                    value={varietyFeedback}
                    onChange={(e) => setVarietyFeedback(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="too_repetitive">{t('training.feedback.variety.too_repetitive') || 'Zu repetitiv'}</option>
                    <option value="optimal">{t('training.feedback.variety.optimal') || 'Optimal'}</option>
                    <option value="too_scattered">{t('training.feedback.variety.too_scattered') || 'Zu verstreut'}</option>
                  </select>
                </div>
              </div>

              {/* Progress & Goals */}
              <div className="mb-4">
                <label className="flex items-center gap-2 mb-2 text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={feltProgress}
                    onChange={(e) => setFeltProgress(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {t('training.feedback.feltProgress') || 'Ich habe Fortschritte gespürt'}
                </label>
                <label className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={wouldRepeat}
                    onChange={(e) => setWouldRepeat(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {t('training.feedback.wouldRepeat') || 'Ich würde diese Session wiederholen'}
                </label>
              </div>

              {/* Open Text Fields */}
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.bestPart') || 'Bester Teil'}
                  </label>
                  <textarea
                    value={bestPart}
                    onChange={(e) => setBestPart(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                    rows={2}
                    placeholder={t('training.feedback.bestPart.placeholder') || 'Was hat dir am besten gefallen?'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.mostChallenging') || 'Herausforderndster Teil'}
                  </label>
                  <textarea
                    value={mostChallenging}
                    onChange={(e) => setMostChallenging(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                    rows={2}
                    placeholder={t('training.feedback.mostChallenging.placeholder') || 'Was war am schwierigsten?'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('training.feedback.suggestions') || 'Vorschläge'}
                  </label>
                  <textarea
                    value={suggestions}
                    onChange={(e) => setSuggestions(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                    rows={3}
                    placeholder={t('training.feedback.suggestions.placeholder') || 'Hast du Verbesserungsvorschläge?'}
                  />
                </div>
              </div>
            </section>

            {/* Exercise Feedback */}
            {exercises.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">
                  {t('training.feedback.exercises.title') || 'Übungs-Bewertungen'}
                </h3>
                <div className="space-y-4">
                  {exercises.map(exercise => {
                    const feedback = exerciseFeedbacks[exercise.id] || {}
                    return (
                      <Card key={exercise.id} className="p-4">
                        <h4 className="font-medium mb-3 text-gray-900 dark:text-slate-100">{exercise.name}</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                              {t('training.feedback.exercise.difficulty') || 'Schwierigkeit'} (1-10)
                            </label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={feedback.difficulty_rating || 5}
                              onChange={(e) => handleExerciseFeedbackChange(exercise.id, 'difficulty_rating', parseInt(e.target.value) || 5)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                              {t('training.feedback.exercise.enjoyment') || 'Spaß'} (1-5)
                            </label>
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              value={feedback.enjoyment_rating || 3}
                              onChange={(e) => handleExerciseFeedbackChange(exercise.id, 'enjoyment_rating', parseInt(e.target.value) || 3)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                              {t('training.feedback.exercise.effectiveness') || 'Effektivität'} (1-10)
                            </label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={feedback.perceived_effectiveness || 5}
                              onChange={(e) => handleExerciseFeedbackChange(exercise.id, 'perceived_effectiveness', parseInt(e.target.value) || 5)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                              {t('training.feedback.exercise.completion') || 'Vollständigkeit'} (%)
                            </label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={feedback.completion_percentage || 100}
                              onChange={(e) => handleExerciseFeedbackChange(exercise.id, 'completion_percentage', parseInt(e.target.value) || 100)}
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                              {t('training.feedback.exercise.notes') || 'Notizen'}
                            </label>
                            <textarea
                              value={feedback.notes || ''}
                              onChange={(e) => handleExerciseFeedbackChange(exercise.id, 'notes', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                              rows={2}
                              placeholder={t('training.feedback.exercise.notes.placeholder') || 'Zusätzliche Notizen zu dieser Übung...'}
                            />
                          </div>
                        </div>
                      </Card>
                    )
                  })}
                </div>
              </section>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end gap-4">
            <Button
              variant="ghost"
              onClick={closeForm}
              disabled={submitting}
            >
              {t('common.cancel') || 'Abbrechen'}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {t('common.submitting') || 'Wird übermittelt...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {submitLabelOverride || t('training.feedback.submit') || 'Feedback absenden'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
